import { CreateButton } from '../../../../components/buttons/CreateButton';
import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { getPartners } from '../../../../api/ApiEndpoints';
import { getAllProductsList } from '../product/ProductService';
import { useTranslation } from 'react-i18next';
import { PlusButton } from '../../../../components/buttons/PlusButton';
import './OrderForm.css';
import { OrderFormTable } from './OrderFormTable';
import { SearchDropdownSelect } from '../../../../components/dropdown/SearchDropdownSelect';
import { useAlert } from 'react-alert';
import { SaveButton } from '../../../../components/buttons/SaveButton';
import { CancelButton } from '../../../../components/buttons/CancelButton';
import { loadOrder } from './OrdersService';

export const OrderForm = ({ onSubmit, onCancel, orderId, isOrdersPage, shortage }) => {

    const { t } = useTranslation();
    const alert = useAlert();
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const [productsData, setProductsData] = useState(null);
    const [products, setProducts] = useState([{ name: t('common:SELECT'), value: -1 }]);
    const [selectedProduct, setSelectedProduct] = useState({ name: t('common:SELECT'), id: -1 });
    const [partnersData, setPartnersData] = useState(null);
    const [partners, setPartners] = useState([{ name: t('common:SELECT'), value: -1 }]);
    const [selectedPartnerId, setSelectedPartnerId] = useState(-1);
    const [productAmount, setProductAmount] = useState(null);
    const [orderProducts, setOrderProducts] = useState([]);

    const handleShow = () => {
        if (products.length === 1) {
            getAllProductsList()
                .then((response) => {
                    setProductsData(response.data);
                    setProducts(products.concat(
                        response.data.map(it => ({ name: it.title, id: it.id })),
                    ));
                })
                .catch(() => alert.error(t('alert:loadingError')));
        }
        if (partners.length === 1) {
            getPartners()
                .then((response) => {
                    setPartnersData(response.data);
                    setPartners(partners.concat(
                        response.data.map(it => ({ name: it.partnerName, value: it.id })),
                    ));
                })
                .catch(() => alert.error(t('alert:loadingError')));
        }
        if (orderId != null) {
            loadOrder(orderId)
                .then((response) => {
                    setSelectedPartnerId(response.data.partnerId);
                    setOrderProducts(response.data.products);
                })
                .catch(() => alert.error(t('alert:loadingError')));
        }
        if (!isOrdersPage && shortage.length > 0) {
            let products = shortage.map(shortageProduct => {
                return {
                    id: shortageProduct.id,
                    title: shortageProduct.title,
                    amount: shortageProduct.amount,
                };
            });
            setOrderProducts(products);
        }
        setShow(true);
    };

    const onClose = () => {
        reset(true);
        onCancel();
        handleClose();
    };

    const submitOrder = () => {
        if (isValidProducts()) {
            let order = {
                id: orderId,
                partnerId: selectedPartnerId,
                status: 'CREATED',
                products: orderProducts,
            };
            onSubmit(order);
            onClose();
        }
    };

    const isValidProducts = () => {
        let result = true;
        orderProducts.forEach((product, index) => {
            let element = document.getElementById('amount-' + index);
            if (product.amount != null && product.amount > 0) {
                element.className = 'form-control';
            } else {
                result = false;
                element.className = 'form-control is-invalid';
            }
        });
        return result;
    };

    const handleKeyUp = (event) => {
        if (event.key === 'Enter') {
            let element = document.getElementById('order-product-amount');
            if (element) {
                let amount = +element.value;
                if (amount > 0) {
                    setProductAmount(amount);
                    addProduct();
                }
            }
        }
    };

    const partnerSelected = (partnerId) => {
        setSelectedPartnerId(+partnerId);
        let partnerProducts = partnersData.find(it => it.id === +partnerId).products;
        if (partnerProducts != null && partnerProducts.length > 0) {
            let productsIds = partnerProducts.map(it => it.id);
            let list = [{ name: t('common:SELECT'), value: -1 }];
            let filteredAndMapped = productsData.filter(product => productsIds.includes(product.id))
                .map(it => ({ name: it.title, id: it.id }));
            setProducts(list.concat(filteredAndMapped));
            if (!isOrdersPage && shortage.length > 0) {
                let products = shortage.filter(product => productsIds.includes(product.id))
                    .map(shortageProduct => {
                        return {
                            id: shortageProduct.id,
                            title: shortageProduct.title,
                            amount: shortageProduct.amount,
                        };
                    });
                setOrderProducts(products);
            }
        } else {
            let list = [{ name: t('common:SELECT'), value: -1 }];
            let filteredAndMapped = productsData.map(it => ({ name: it.title, id: it.id }));
            setProducts(list.concat(filteredAndMapped));
            if (!isOrdersPage && shortage.length > 0) {
                let products = shortage.map(shortageProduct => {
                    return {
                        id: shortageProduct.id,
                        title: shortageProduct.title,
                        amount: shortageProduct.amount,
                    };
                });
                setOrderProducts(products);
            }
        }
    };

    const reset = (fullReset) => {
        if (fullReset) {
            setSelectedPartnerId(-1);
            setOrderProducts([]);
        }
        setSelectedProduct({ name: t('common:SELECT'), id: -1 });
        setProductAmount(null);
        let element = document.getElementById('order-product-amount');
        if (element) {
            element.value = null;
        }
    };

    const onProductAmountInput = (amount) => {
        setProductAmount(+amount);
    };

    const addProduct = () => {
        if (selectedProduct) {
            let orderProduct = {
                id: selectedProduct.id,
                title: selectedProduct.name,
                amount: productAmount,
            };
            setOrderProducts(prev => {
                let items = [...prev, orderProduct];
                return items.sort((a, b) => a.title > b.title ? 1 : -1);
            });
            reset(false);
        }
    };

    const removeOrderProduct = (index) => {
        setOrderProducts((prevProducts) =>
            prevProducts.filter((product, idx) => idx !== index),
        );
    };

    const copyOrderText = () => {
        let textToCopy = t('order:form.newOrder') + ':';
        orderProducts.forEach(product => {
            textToCopy = textToCopy + '\n' + product.title + ' ' + product.amount + t('common:unit.kg');
        });
        navigator.clipboard.writeText(textToCopy)
            .then(() => alert.success(t('alert:copyToClipboard')))
            .catch(() => alert.error(t('alert:copyToClipboardError')));
    };

    return (
        <>
            {isOrdersPage
                ? <CreateButton onClick={handleShow} />
                : <button className={'button--main success-button'} onClick={handleShow}
                          id={'orderButton'}>{t('order:orderButton')}</button>}
            <Modal show={show} onHide={onClose} backdrop="static">
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>{orderId != null ? t('order:form.updateOrder') : t('order:form.newOrder')}</Modal.Title>
                        <select className={'form-select'}
                                id={'order-partner-select'}
                                value={selectedPartnerId}
                                onChange={(e) => partnerSelected(e.target.value)}>
                            {partners.map((partner) => {
                                return (
                                    <option value={partner.value} key={partner.name}>
                                        {partner.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {selectedPartnerId !== -1
                        ? <div>
                            <div className={'order-form-container'}>
                                <div className={'mb-3'} style={{ width: '60%' }}>
                                    <SearchDropdownSelect title={t('order:form.product')}
                                                          id={selectedProduct.id}
                                                          onSelect={(id) => setSelectedProduct(products.filter(it => it.id === id)[0])}
                                                          data={products.map(it => ({ id: it.id, name: it.name }))}>
                                    </SearchDropdownSelect>
                                </div>
                                <div className={'mb-3'} style={{ width: '30%' }}>
                                    <span>{t('order:form.amount')}</span>
                                    <input type={'number'}
                                           className={'form-control'}
                                           id={'order-product-amount'}
                                           defaultValue={productAmount}
                                           onKeyUp={handleKeyUp}
                                           onChange={(e) => onProductAmountInput(e.target.value)} />
                                </div>
                                <PlusButton disabled={selectedProduct.id === -1 || productAmount <= 0}
                                            onClick={addProduct}></PlusButton>
                            </div>
                            <OrderFormTable products={orderProducts} onRemove={removeOrderProduct}></OrderFormTable>
                            <div className={'center'}>
                                <SaveButton disabled={selectedPartnerId.id === -1 || orderProducts <= 0}
                                            onClick={submitOrder}>
                                </SaveButton>
                                <CancelButton onClick={onClose}></CancelButton>
                                {orderProducts.length === 0
                                    ? <></>
                                    : <button className={'button--main success-button'}
                                              onClick={copyOrderText}>{t('order:form.copyOrderText')}
                                    </button>}
                            </div>
                        </div>
                        : <></>}
                </Modal.Body>
            </Modal>
        </>
    );
};